import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking session, false = not logged in, object = logged-in profile
  const [user, setUser] = useState(null);

  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
    if (error) throw error;
    return data;
  };

  useEffect(() => {
    // Use onAuthStateChange as the single source of truth
    // It fires immediately with the current session on mount (INITIAL_SESSION event)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          try {
            let profile = await fetchProfile(session.user.id);

            // Force DiceBear avatar for Google OAuth users still using Google profile pics
            const isGoogleAvatar = profile.avatar_url &&
              (profile.avatar_url.includes("googleusercontent.com") ||
               profile.avatar_url.includes("googleapis.com") ||
               profile.avatar_url.includes("google.com/a/"));
            const hasNoAvatar = !profile.avatar_url;

            if (isGoogleAvatar || hasNoAvatar) {
              const diceBearUrl = `https://api.dicebear.com/7.x/notionists/svg?seed=${session.user.id}&backgroundColor=ffd600,ff3333,2b5cff`;
              await supabase.from("profiles").update({ avatar_url: diceBearUrl }).eq("id", session.user.id);
              profile = { ...profile, avatar_url: diceBearUrl };
            }

            setUser(profile);
          } catch {
            // Profile might not exist yet (e.g. new Google OAuth user)
            // Set a minimal user object so ProtectedRoute doesn't bounce
            setUser({
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.name || session.user.email?.split("@")[0] || "Player",
              avatar_url: `https://api.dicebear.com/7.x/notionists/svg?seed=${session.user.id}&backgroundColor=ffd600,ff3333,2b5cff`,
              role: "player",
              engagement_score: 0,
              comeback_score: 0,
              goal_amount: 10000,
              current_amount: 0,
              methods: [],
              participant_id: "P-" + session.user.id.replace(/-/g, "").slice(0, 8).toUpperCase(),
            });
          }
        } else {
          setUser(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Eagerly fetch & set the profile so nav('/home') doesn't race with onAuthStateChange
    try {
      const profile = await fetchProfile(data.user.id);
      setUser(profile);
      return profile;
    } catch {
      return data.user;
    }
  };

  const register = async ({ email, password, name, goal_amount, strategy_summary }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    if (error) throw error;
    // Trigger creates the profile row — give it a moment then patch extra fields
    if (data.user) {
      await new Promise((r) => setTimeout(r, 800));
      await supabase.from("profiles").upsert({
        id:               data.user.id,
        email:            email.toLowerCase(),
        name,
        goal_amount:      parseFloat(goal_amount) || 10000,
        strategy_summary: strategy_summary || "",
        participant_id:   "P-" + data.user.id.replace(/-/g, "").slice(0, 8).toUpperCase(),
      });
      try {
        const profile = await fetchProfile(data.user.id);
        setUser(profile);
      } catch (_) {}
    }
    return data.user;
  };

  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(false);
  };

  const updateUser = async (updates) => {
    const { data, error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", user.id)
      .select()
      .single();
    if (error) throw error;
    setUser(data);
    return data;
  };

  const refresh = async () => {
    if (!user?.id) return null;
    try {
      const profile = await fetchProfile(user.id);
      setUser(profile);
      return profile;
    } catch { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, loginWithGoogle, logout, refresh, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
